import { FormEvent, useState } from 'react';
import { Trash2Icon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
    useAddHiddenSample,
    useHiddenSamples,
    useRemoveHiddenSample,
    useSetHiddenSample,
} from '@/services/sample.service';
import BackofficePage, {
    DataCard,
    DataTable,
    TH,
    TR,
    TD,
} from '../../components/BackofficePage';
import { saveErrorMessage } from './saveError';

const INPUT =
    'bg-[#0f141a] border border-[#1e242a] rounded-md px-3 py-2 text-sm text-[#e5e7e3] placeholder-[#6b7480] focus:outline-none focus:border-[#2d6a4f]';

function HideToggle({
    hidden,
    onClick,
}: {
    hidden: boolean;
    onClick: () => void;
}) {
    return (
        <button
            onClick={onClick}
            title="Click to switch"
            className={
                'px-3 py-1 rounded-md text-xs font-medium border transition ' +
                (hidden
                    ? 'bg-[#2a1e1e] border-[#7f2d2d] text-[#f08c8c]'
                    : 'bg-[#161c22] border-[#1e242a] text-[#a8b0bb] hover:text-white')
            }
        >
            {hidden ? 'Hidden' : 'Visible'}
        </button>
    );
}

export default function BackOfficeHide() {
    const { data, isLoading, error } = useHiddenSamples();
    const add = useAddHiddenSample();
    const setHidden = useSetHiddenSample();
    const remove = useRemoveHiddenSample();
    const [projectId, setProjectId] = useState('');
    const [sampleId, setSampleId] = useState('');

    const description =
        'Samples whose analyses only super admins can see. Hidden roots: the roots layer and the curves are withheld from project members. Hidden fauna: the fauna analysis is withheld, the sample shows as having none.';

    if (isLoading)
        return (
            <BackofficePage title="Hide" description="Loading…">
                <Skeleton className="w-full h-[400px] bg-[#1e242a]" />
            </BackofficePage>
        );
    if (error)
        return (
            <BackofficePage title="Hide" description="Error">
                <div className="text-red-400 text-sm">{error.toString()}</div>
            </BackofficePage>
        );

    const onAdd = (e: FormEvent) => {
        e.preventDefault();
        add.mutate(
            { projectId: projectId.trim(), sampleId: sampleId.trim() },
            {
                onSuccess: () => {
                    setProjectId('');
                    setSampleId('');
                },
            },
        );
    };

    const rows = data ?? [];

    return (
        <BackofficePage title="Hide" description={description}>
            <form onSubmit={onAdd} className="mb-4 flex flex-wrap gap-2">
                <input
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    placeholder="projectId"
                    className={`${INPUT} w-72 font-mono`}
                />
                <input
                    value={sampleId}
                    onChange={(e) => setSampleId(e.target.value)}
                    placeholder="sampleId"
                    className={`${INPUT} w-72 font-mono`}
                />
                <button
                    type="submit"
                    disabled={
                        !projectId.trim() || !sampleId.trim() || add.isPending
                    }
                    className="px-4 py-2 rounded-md text-sm font-medium bg-[#2d6a4f] text-white hover:bg-[#357a5b] disabled:opacity-40 transition"
                >
                    Add
                </button>
            </form>
            {add.error && (
                <div className="mb-4 text-red-400 text-sm">
                    {saveErrorMessage(add.error)}
                </div>
            )}
            <DataCard>
                <DataTable>
                    <thead className="bg-[#0f141a]">
                        <tr>
                            <TH>Sample</TH>
                            <TH align="center">Project</TH>
                            <TH align="center">Roots analysis</TH>
                            <TH align="center">Fauna analysis</TH>
                            <TH align="center"> </TH>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <TR>
                                <TD>
                                    <span className="text-[#6b7480]">
                                        No hidden sample.
                                    </span>
                                </TD>
                            </TR>
                        )}
                        {rows.map((r) => (
                            <TR key={r.sampleId}>
                                <TD>
                                    <div>{r.name}</div>
                                    <div className="text-[10px] font-mono text-[#6b7480]">
                                        {r.sampleId}
                                    </div>
                                </TD>
                                <TD align="center">
                                    <div>{r.projectName}</div>
                                    <div className="text-[10px] font-mono text-[#6b7480]">
                                        {r.projectId}
                                    </div>
                                </TD>
                                <TD align="center">
                                    <HideToggle
                                        hidden={r.hideRoots}
                                        onClick={() =>
                                            setHidden.mutate({
                                                sampleId: r.sampleId,
                                                hideRoots: !r.hideRoots,
                                            })
                                        }
                                    />
                                </TD>
                                <TD align="center">
                                    <HideToggle
                                        hidden={r.hideFauna}
                                        onClick={() =>
                                            setHidden.mutate({
                                                sampleId: r.sampleId,
                                                hideFauna: !r.hideFauna,
                                            })
                                        }
                                    />
                                </TD>
                                <TD align="center">
                                    <button
                                        onClick={() =>
                                            remove.mutate(r.sampleId)
                                        }
                                        title="Remove from the list"
                                        className="p-1.5 rounded-md text-[#6b7480] hover:text-red-400 hover:bg-[#161c22] transition"
                                    >
                                        <Trash2Icon className="h-4 w-4" />
                                    </button>
                                </TD>
                            </TR>
                        ))}
                    </tbody>
                </DataTable>
            </DataCard>
        </BackofficePage>
    );
}
